import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from '../components/Pagination';
import { useSearchExport } from '../context/SearchExportContext';
import { getCoupons } from '../redux/couponSlice';
import { createCoupon } from '../services/api';
import ExportDropdown from '../context/ExportDropdown';
import RecordsLoader from '../components/RecordsLoader';

const PLAN_OPTIONS = [
    { value: 2, label: 'Pro Plan' },
    { value: 3, label: 'Ultimate Plan' },
];

const CouponsList = () => {
    const dispatch = useDispatch();
    const { coupons, status } = useSelector((state) => state.coupons);
    const { filterData } = useSearchExport();

    const [currentPage, setCurrentPage] = useState(1);
    const couponsPerPage = 50;
    const [showForm, setShowForm] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [discountPercent, setDiscountPercent] = useState('');
    const [planId, setPlanId] = useState(2);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        dispatch(getCoupons());
    }, [dispatch]);

    const currentcoupons = filterData(coupons)
        .slice((currentPage - 1) * couponsPerPage, currentPage * couponsPerPage);

    const handleCreate = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');
        setSubmitting(true);
        try {
            const res = await createCoupon({
                coupon_code: couponCode.trim().toUpperCase(),
                discount_percent: Number(discountPercent),
                plan_id: Number(planId),
            });
            if (res.data?.status === 1) {
                setFormSuccess(res.data.message || 'Coupon created');
                setCouponCode('');
                setDiscountPercent('');
                setPlanId(2);
                setShowForm(false);
                dispatch(getCoupons());
            } else {
                setFormError(res.data?.message || 'Failed to create coupon');
            }
        } catch (err) {
            setFormError(err.response?.data?.message || 'Failed to create coupon');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'idle' && <RecordsLoader text="Loading Coupons, please hold on..." />}

            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h1 className="text-2xl text-black font-bold">Coupons List</h1>
                <button
                    type="button"
                    onClick={() => {
                        setShowForm((v) => !v);
                        setFormError('');
                        setFormSuccess('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                    {showForm ? 'Cancel' : '+ Create Coupon'}
                </button>
            </div>

            {formSuccess && (
                <p className="mb-4 text-green-600 font-medium">{formSuccess}</p>
            )}

            {showForm && (
                <form
                    onSubmit={handleCreate}
                    className="mb-6 p-4 bg-white shadow-md rounded-lg border border-gray-200"
                >
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">New Coupon</h2>
                    {formError && <p className="mb-3 text-red-600 text-sm">{formError}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Coupon Code
                            </label>
                            <input
                                type="text"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                                placeholder="e.g. ADMIN50"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Discount (%)
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={100}
                                value={discountPercent}
                                onChange={(e) => setDiscountPercent(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                                placeholder="1-100"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Plan
                            </label>
                            <select
                                value={planId}
                                onChange={(e) => setPlanId(Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                            >
                                {PLAN_OPTIONS.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                        {submitting ? 'Creating...' : 'Create Coupon'}
                    </button>
                </form>
            )}

            <ExportDropdown data={coupons} fileName="coupons" />

            {status === 'failed' && <p>Error loading coupons</p>}

            {status === 'succeeded' && (
                <>
                    <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="px-4 py-2">ID</th>
                                <th className="px-4 py-2">Coupon Code</th>
                                <th className="px-4 py-2">Discount Percent</th>
                                <th className="px-4 py-2">Allocated Plan</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentcoupons.length > 0 ? (
                                currentcoupons.map((coupon) => (
                                    <tr
                                        key={coupon.id}
                                        className="border-b cursor-pointer hover:bg-gray-100"
                                    >
                                        <td className="px-4 py-2">{coupon.id}</td>
                                        <td className="px-4 py-2">{coupon.coupon_code}</td>
                                        <td className="px-4 py-2">{coupon.discount_percent}</td>
                                        <td className="px-4 py-2">
                                            {coupon.plan_id === 3
                                                ? 'Ultimate Plan'
                                                : coupon.plan_id === 2
                                                  ? 'Pro Plan'
                                                  : 'Free Plan'}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-4">
                                        No coupons Found!
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {coupons.length > couponsPerPage && (
                        <Pagination
                            usersPerPage={couponsPerPage}
                            totalUsers={coupons.length}
                            paginate={setCurrentPage}
                            currentPage={currentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default CouponsList;
